import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { startSOADrill, submitSOAAnswer, completeSOADrill } from '../api/soa';

/**
 * EMZI NEXUS V6.3 - SOA Drill UI
 * 5-domain adaptive assessment
 * Mobile-first, 390×844, no scroll
 * EMZI CI: Blue/White/Orange
 */

const SOADrill = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const t = useLang();
  
  // State management
  const [step, setStep] = useState('context'); // context, drill, results
  const [sessionId, setSessionId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentDomain, setCurrentDomain] = useState(null);
  const [answer, setAnswer] = useState('');
  const [progress, setProgress] = useState({ completed: 0, total: 5 });
  const [evaluation, setEvaluation] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Context form state
  const [context, setContext] = useState({
    objective: 'Sales',
    product: { name: '', usp: '' },
    audience: { age_group: '', gender: '', interest: '' },
    brand_voice: ''
  });

  const handleContextChange = (field, value) => {
    setContext(prev => ({ ...prev, [field]: value }));
  };

  const handleStartDrill = async () => {
    if (!context.product.name) {
      setError(t('soaDrill.errors.productNameRequired'));
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await startSOADrill({
        user_id: user?.user_id || 'demo_user',
        context: context
      });
      
      setSessionId(response.session_id);
      setCurrentQuestion(response.question);
      setCurrentDomain(response.current_domain);
      setProgress({ completed: 0, total: response.total_domains });
      setStep('drill');
    } catch (err) {
      setError(err.message || t('soaDrill.errors.startFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAnswer = async () => {
    if (!answer.trim()) {
      setError(t('soaDrill.errors.answerRequired'));
      return;
    }
    
    if (answer.trim().split(' ').length < 10) {
      setError(t('soaDrill.errors.answerTooShort'));
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await submitSOAAnswer({
        session_id: sessionId,
        answer: answer
      });
      
      setEvaluation(response.evaluation);
      setProgress(response.progress);
      
      // Brief pause to show evaluation
      setTimeout(() => {
        if (response.is_complete) {
          handleCompleteDrill();
        } else {
          setCurrentQuestion(response.next_question.question);
          setCurrentDomain(response.next_question.domain);
          setAnswer('');
          setEvaluation(null);
        }
      }, 3000);
      
    } catch (err) {
      setError(err.message || t('soaDrill.errors.submitFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteDrill = async () => {
    setLoading(true);
    
    try {
      const response = await completeSOADrill(sessionId);
      setResults(response.results);
      setStep('results');
    } catch (err) {
      setError(err.message || t('soaDrill.errors.completeFailed'));
    } finally {
      setLoading(false);
    }
  };

  const renderContextForm = () => (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0e67a2] to-[#0a5a8a] text-white p-4 pb-safe">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
            <span className="text-2xl">🧩</span>
          </div>
          <div>
            <h1 className="text-lg font-bold">{t('soaDrill.title')}</h1>
            <p className="text-xs opacity-90">{t('soaDrill.subtitle')}</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 pb-safe">
        <div className="max-w-md mx-auto">
          <div className="mb-4">
            <h2 className="text-base font-bold text-gray-800 mb-2">{t('soaDrill.contextSetup')}</h2>
            <p className="text-xs text-gray-600">{t('soaDrill.contextDescription')}</p>
          </div>

          {/* Objective */}
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('soaDrill.objective')}</label>
            <select
              value={context.objective}
              onChange={(e) => handleContextChange('objective', e.target.value)}
              className="w-full h-11 px-3 border border-gray-300 rounded-lg text-sm"
            >
              <option value="Sales">{t('soaDrill.objectives.sales')}</option>
              <option value="Awareness">{t('soaDrill.objectives.awareness')}</option>
              <option value="Engagement">{t('soaDrill.objectives.engagement')}</option>
              <option value="Service">{t('soaDrill.objectives.service')}</option>
            </select>
          </div>

          {/* Product Name */}
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('soaDrill.productNameRequired')}</label>
            <input
              type="text"
              value={context.product.name}
              onChange={(e) => handleContextChange('product', { ...context.product, name: e.target.value })}
              placeholder={t('soaDrill.productPlaceholder')}
              className="w-full h-11 px-3 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          {/* Target Audience */}
          <div className="mb-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('soaDrill.targetAudience')}</label>
            <input
              type="text"
              value={context.audience.age_group}
              onChange={(e) => handleContextChange('audience', { ...context.audience, age_group: e.target.value })}
              placeholder={t('soaDrill.audiencePlaceholder')}
              className="w-full h-11 px-3 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          {/* Brand Voice */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('soaDrill.brandVoice')}</label>
            <input
              type="text"
              value={context.brand_voice}
              onChange={(e) => handleContextChange('brand_voice', e.target.value)}
              placeholder={t('soaDrill.brandVoicePlaceholder')}
              className="w-full h-11 px-3 border border-gray-300 rounded-lg text-sm"
            />
          </div>

          {error && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
              {error}
            </div>
          )}

          <button
            onClick={handleStartDrill}
            disabled={loading}
            className="w-full h-14 bg-gradient-to-r from-[#0e67a2] to-[#0a5a8a] text-white font-bold rounded-xl text-base disabled:opacity-50"
          >
            {loading ? t('soaDrill.starting') : t('soaDrill.startSOADrillButton')}
          </button>

          <button
            onClick={() => navigate(-1)}
            className="w-full mt-2 h-12 border border-gray-300 text-gray-700 font-medium rounded-xl text-sm"
          >
            {t('soaDrill.cancel')}
          </button>
        </div>
      </div>
    </div>
  );

  const renderDrillQuestion = () => (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0e67a2] to-[#0a5a8a] text-white p-4 pb-safe">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-lg font-bold">{currentDomain} {t('soaDrill.awareness')}</h1>
          <span className="text-sm bg-white/20 px-3 py-1 rounded-full">
            {progress.completed + 1}/{progress.total}
          </span>
        </div>
        {/* Progress bar */}
        <div className="w-full bg-white/20 rounded-full h-2">
          <div
            className="bg-white h-2 rounded-full transition-all duration-300"
            style={{ width: `${((progress.completed + 1) / progress.total) * 100}%` }}
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 pb-safe">
        <div className="max-w-md mx-auto">
          {/* Question */}
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-sm font-medium text-gray-800">{currentQuestion}</p>
          </div>

          {/* Evaluation (if exists) */}
          {evaluation && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">✅</span>
                <span className="text-lg font-bold text-green-700">{t('soaDrill.score')}: {evaluation.score}/100</span>
              </div>
              <p className="text-xs text-gray-700 mb-2">{evaluation.feedback}</p>
              {evaluation.strengths && evaluation.strengths.length > 0 && (
                <div className="text-xs text-gray-600">
                  <strong>{t('soaDrill.strengths')}:</strong> {evaluation.strengths.join(', ')}
                </div>
              )}
            </div>
          )}

          {/* Answer input */}
          {!evaluation && (
            <>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('soaDrill.yourAnswer')}</label>
              <textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder={t('soaDrill.answerPlaceholder')}
                className="w-full h-32 p-3 border border-gray-300 rounded-lg text-sm resize-none mb-3"
              />

              <div className="text-xs text-gray-500 mb-3">
                {t('soaDrill.wordCount')}: {answer.trim().split(' ').filter(w => w).length}
              </div>

              {error && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-600">
                  {error}
                </div>
              )}

              <button
                onClick={handleSubmitAnswer}
                disabled={loading}
                className="w-full h-14 bg-gradient-to-r from-[#0e67a2] to-[#0a5a8a] text-white font-bold rounded-xl text-base disabled:opacity-50"
              >
                {loading ? t('soaDrill.evaluating') : t('soaDrill.submitAnswer')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderResults = () => (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0e67a2] to-[#0a5a8a] text-white p-4 pb-safe">
        <h1 className="text-lg font-bold">{t('soaDrill.drillComplete')}</h1>
        <p className="text-xs opacity-90">{t('soaDrill.assessmentResults')}</p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 pb-safe">
        <div className="max-w-md mx-auto">
          {/* Overall Score */}
          <div className="mb-4 p-6 bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-300 rounded-xl text-center">
            <div className="text-5xl font-black text-[#0e67a2] mb-2">
              {results?.average_score}%
            </div>
            <div className="text-sm text-gray-700 font-medium">{t('soaDrill.overallScore')}</div>
            <div className="mt-2 inline-block px-4 py-1 bg-white rounded-full text-xs font-bold text-[#0e67a2]">
              {results?.performance_tier?.toUpperCase()}
            </div>
          </div>

          {/* BP Earned */}
          <div className="mb-4 p-4 bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-300 rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-600">{t('soaDrill.brainPointsEarned')}</div>
                <div className="text-3xl font-black text-orange-600">+{results?.total_bp} BP</div>
              </div>
              <div className="text-4xl">🧠</div>
            </div>
            {results?.bp_breakdown?.streak_multiplier > 1 && (
              <div className="mt-2 text-xs text-orange-700">
                {t('soaDrill.streakBonus')}: {((results.bp_breakdown.streak_multiplier - 1) * 100).toFixed(0)}%
              </div>
            )}
          </div>

          {/* Domain Scores */}
          <div className="mb-4">
            <h3 className="text-sm font-bold text-gray-800 mb-2">{t('soaDrill.domainBreakdown')}</h3>
            {Object.entries(results?.domain_scores || {}).map(([domain, score]) => (
              <div key={domain} className="mb-2">
                <div className="flex justify-between text-xs text-gray-700 mb-1">
                  <span>{domain}</span>
                  <span className="font-bold">{score}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-[#0e67a2] h-2 rounded-full"
                    style={{ width: `${score}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Coaching Summary */}
          {results?.coaching_summary && (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-300 rounded-xl">
              <h3 className="text-sm font-bold text-gray-800 mb-2">{t('soaDrill.coachFeedback')}</h3>
              <p className="text-xs text-gray-700 mb-3">{results.coaching_summary.summary}</p>
              
              {results.coaching_summary.key_takeaways && (
                <div className="mb-2">
                  <div className="text-xs font-bold text-gray-700 mb-1">{t('soaDrill.keyTakeaways')}</div>
                  <ul className="text-xs text-gray-600 space-y-1">
                    {results.coaching_summary.key_takeaways.map((item, idx) => (
                      <li key={`item-${idx}`}>• {item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Suggested Course */}
          {results?.weakest_domain && (
            <div className="mb-4 p-4 bg-purple-50 border border-purple-300 rounded-xl">
              <h3 className="text-sm font-bold text-gray-800 mb-1">{t('soaDrill.recommendedLearning')}</h3>
              <p className="text-xs text-gray-600 mb-2">
                {t('soaDrill.strengthenSkills')} <strong>{results.weakest_domain}</strong> {t('soaDrill.skills')}:
              </p>
              <div className="text-xs font-medium text-purple-700">
                {t('soaDrill.nexusAcademy')}: {results.weakest_domain} {t('soaDrill.mastery')}
              </div>
            </div>
          )}

          <button
            onClick={() => navigate('/dashboard')}
            className="w-full h-14 bg-gradient-to-r from-[#0e67a2] to-[#0a5a8a] text-white font-bold rounded-xl text-base"
          >
            {t('soaDrill.backToDashboard')}
          </button>

          <button
            onClick={() => {
              setStep('context');
              setSessionId(null);
              setCurrentQuestion(null);
              setAnswer('');
              setEvaluation(null);
              setResults(null);
            }}
            className="w-full mt-2 h-12 border border-gray-300 text-gray-700 font-medium rounded-xl text-sm"
          >
            {t('soaDrill.takeAnotherDrill')}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-dvh w-full overflow-hidden bg-white">
      {step === 'context' && renderContextForm()}
      {step === 'drill' && renderDrillQuestion()}
      {step === 'results' && renderResults()}
    </div>
  );
};

export default SOADrill;
